import React, { FC, Fragment, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Menu, MenuItem, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { GetFeedGroupFragment, Group } from 'api';
import { generateLink } from 'Links';
import { useContent } from 'hooks';

interface IProps {
  groups?: GetFeedGroupFragment[];
}

const SHOWN_GROUPS = 2;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'inline-block',
  },
  moreText: {
    display: 'inline-block',
    marginLeft: 4,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  menuItem: {
    '&:hover, &:focus': {
      textDecoration: 'underline',
      backgroundColor: theme.palette.common.white,
    },
  },
}));

const Groups: FC<IProps> = ({ groups }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const content = useContent();

  groups = !!groups?.length ? groups : content?.groups ?? [];

  const [anchorEl, setAnchorEl] = useState(null);

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function renderGroupsList() {
    const lastIndex = Math.min(groups.length - 1, SHOWN_GROUPS - 1);
    const firstTwo = groups.slice(0, SHOWN_GROUPS);

    return firstTwo.map(({ id, title, color_map }, index) => (
      <Fragment key={id}>
        <Link component={generateLink(`/group/${id}`)}>{title}</Link>
        {index !== lastIndex && <span>, </span>}
      </Fragment>
    ));
  }

  function renderMenuItem(group: Group) {
    const { id, title } = group;

    const itemProps = {
      key: id,
      component: generateLink(`/group/${id}`),
      className: classes.menuItem,
    };

    return <MenuItem {...itemProps}>{title}</MenuItem>;
  }

  function renderGroupsMenu() {
    const remainingGroups = groups.slice(SHOWN_GROUPS);

    return (
      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}>
        <div>{remainingGroups.map(renderMenuItem)}</div>
      </Menu>
    );
  }

  function renderMoreLink() {
    return (
      <Typography component="span" className={classes.moreText} onClick={handleClick}>
        {t('content:xMoreGroups', { groups: groups.length - SHOWN_GROUPS })}
      </Typography>
    );
  }

  if (!groups || !groups.length) {
    return null;
  }

  const needsMore = groups.length > SHOWN_GROUPS;

  return (
    <Typography className={classes.root}>
      {renderGroupsList()}
      {needsMore && renderMoreLink()}
      {renderGroupsMenu()}
    </Typography>
  );
};

Groups.defaultProps = {
  groups: [],
};

export default Groups;
